import {
  getEffectiveCouponCode,
  shouldAddMidTermFeature,
} from '@/views/config/coupon/helper/utils';

/**
 * @typedef {Object} CouponPayload
 * @property {boolean} fromCampaignLink - Whether coupon is from campaign link
 * @property {string} code - Coupon code (instanceId or original couponCode)
 * @property {boolean} [midTermFeature] - Only present when shouldAddMidTermFeature returns true
 */

/**
 * @typedef {Object} BuyNowCouponPayload
 * @property {boolean} fromCampaignLink - Whether coupon is from campaign link
 * @property {string} code - Coupon code
 * @property {boolean} [fromZoomBundleJointsale] - Only for soft bundle scenarios
 */

/**
 * @typedef {Object} TaxEstimateCouponPayload
 * @property {boolean} fromCampaignLink - Whether coupon is from campaign link
 * @property {string} code - Coupon code
 * @property {boolean} [midTermFeature] - Only present when shouldAddMidTermFeature returns true
 */

/**
 * @typedef {Object} VuexGetters
 * @property {Object} couponState - Coupon state object
 * @property {string} couponState.couponCode - Current coupon code
 * @property {Object} appliedCouponInfo - Applied coupon info from FTL
 * @property {boolean} appliedCouponInfo.midTermFeature - Whether mid-term feature is enabled
 * @property {string} [appliedCouponInfo.instanceId] - Instance ID (priority over couponCode)
 * @property {string} [appliedCouponInfo.couponCode] - Original coupon code
 * @property {boolean} fromCampaignLink - Whether from campaign link
 * @property {Object} couponPayload - Full coupon payload (for buildFull)
 */

/**
 * Coupon Payload Builder
 * Builds coupon payload for different flows with shared logic
 *
 * This class centralizes coupon payload construction logic to:
 * 1. Share common logic (e.g., midTermFeature detection)
 * 2. Support flow-specific customization
 * 3. Maintain backward compatibility with existing API structures
 */
class CouponPayloadBuilder {
  /**
   * @param {VuexGetters} getters - Vuex getters object
   */
  constructor(getters) {
    this.getters = getters;
    this.couponState = getters.couponState || {};
    this.appliedCouponInfo = getters.appliedCouponInfo;
  }

  // ============ Public Methods (Shared Logic) ============

  /**
   * Get effective coupon code from appliedCouponInfo
   * Priority: instanceId > couponCode
   * - instanceId: Generated when user first redeems a coupon, stored by backend
   * - couponCode: Original coupon code before distribution
   * @returns {string} Effective coupon code
   */
  getAppliedEffectiveCouponCode() {
    return getEffectiveCouponCode(this.appliedCouponInfo);
  }

  /**
   * Get current coupon code from couponState
   * @returns {string} Current coupon code
   */
  getCouponCode() {
    return this.couponState?.couponCode || '';
  }

  /**
   * Check if midTermFeature should be added to coupon payload
   * Uses shared utility function from utils.js
   * @returns {boolean} Whether midTermFeature should be added
   */
  shouldAddMidTermFeature() {
    return shouldAddMidTermFeature(this.getCouponCode(), this.appliedCouponInfo);
  }

  /**
   * Get midTermFeature field object (for spread operator)
   * @returns {Object} Object with midTermFeature: true, or empty object
   */
  getMidTermFeatureField() {
    return this.shouldAddMidTermFeature() ? { midTermFeature: true } : {};
  }

  // ============ Flow-Specific Builders ============

  /**
   * Build coupon payload for freeTrialUpgrade API
   * Used by: ADD/index.js, UPGRADE/index.js, UPGRADE/pbx2prop.js
   * @returns {CouponPayload} Coupon payload for freeTrialUpgrade
   */
  buildForFreeTrialUpgrade() {
    return {
      fromCampaignLink: this.getters.fromCampaignLink,
      code: this.getCouponCode(),
      ...this.getMidTermFeatureField(),
    };
  }

  /**
   * Build coupon payload for BUY_NOW flow (submitOrder API)
   * Used by: BUY_NOW/index.js
   * Note: BUY_NOW is first-time purchase, appliedCouponInfo does not exist,
   *       so midTermFeature is never applicable
   * @param {Object} extraFields - Extra fields to include (e.g., { fromZoomBundleJointsale: true })
   * @returns {BuyNowCouponPayload} Coupon payload for BUY_NOW
   */
  buildForBuyNow(extraFields = {}) {
    return {
      fromCampaignLink: this.getters.fromCampaignLink,
      code: this.getCouponCode(),
      ...extraFields,
    };
  }

  /**
   * Build coupon payload for tax estimate API (/opc/tax/estimate)
   * Used by: tax.js taxPayload getter
   * Uses same midTermFeature logic as other mid-contract APIs
   * @returns {TaxEstimateCouponPayload} Coupon payload for tax estimate
   */
  buildForTaxEstimate() {
    return {
      fromCampaignLink: this.couponState?.fromCampaignLink ?? false,
      code: this.getCouponCode(),
      ...this.getMidTermFeatureField(),
    };
  }

  /**
   * Build full coupon payload (for updatePlansCheckout API)
   * Used by: EDIT, DOWNGRADE, SWITCH, ALIGN, MIX_BUY, ADD/upsell, etc.
   * This is equivalent to couponPayload getter in shopping-cart.js
   * @returns {Object} Full coupon payload
   */
  buildFull() {
    return this.getters.couponPayload;
  }
}

/**
 * Factory function to create CouponPayloadBuilder instance
 * @param {VuexGetters} getters - Vuex getters object
 * @returns {CouponPayloadBuilder} Builder instance
 */
export const createCouponPayloadBuilder = (getters) => {
  return new CouponPayloadBuilder(getters);
};

export default CouponPayloadBuilder;
